import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	Button,
	Dropdown,
	DropdownMenuButton,
	IconButton,
	Loading,
} from "@alm/flow-ds/components";
import Element from "./Element";
import useForm from "hooks/useForm";
import PageHeader from "components/PageHeader";
import { useTranslation } from "react-i18next";
import TaskPageTemplate from "components/Templates/TaskPageTemplate";
import { WorkbenchLayer } from "components/Workbench";
import { WorkbenchContext } from "contexts/WorkbenchContext";
import TaskContext, { ITaskContext } from "contexts/TaskContext";
import UnsavedChanges from "./UnsavedChanges";
import LinkedForm from "./Default/LinkedForm";
import useLinkedForm from "hooks/useLinkedForm";
import TaskError from "components/ActivityForm/TaskError";
import TaskInfo from "components/Tasks/TaskInfo";
import { handleDate } from "lib/dates";
import Breadcrumb from "components/Breadcrumb";
import { State } from "data";
import { useSelector } from "react-redux";

interface Props {
	fid: string;
	workbenchIndex: number;
}

type FormLayout = {
	fullWidth: boolean;
	maxWidth: number | null;
};

export const useMaxFormWidth = (formLayout: FormLayout) => {
	type MaxWidth = number | string;
	const [maxWidth, setMaxWidth] = useState<MaxWidth>("auto");
	const [maxFormWidth, setMaxFormWidth] = useState<MaxWidth>("auto");

	useEffect(() => {
		if (formLayout?.maxWidth && formLayout?.fullWidth) {
			setMaxFormWidth(formLayout.maxWidth);
			setMaxWidth(formLayout.maxWidth * 1.1);
		}
	}, [formLayout]);

	return { maxWidth, maxFormWidth };
};

function TaskForm({ fid, workbenchIndex }: Props) {
	const { t, i18n } = useTranslation();

	const formLayout = useSelector(
		(state: State) => state?.forms[fid]?.server?.formLayout
	);

	const [closePrompt, setClosePrompt] = useState(false);

	// Grab the data we need to render the task form
	const form = useForm(fid);

	// Get the linked form
	const linked = useLinkedForm(fid, 0);

	const blockFeedback = useRef<HTMLDivElement | null>(null);

	// Scroll actice or not
	const [isScrollActive, setIsScrollActive] = useState(false);

	// Scrolling element
	const formWindow = document.querySelector(`.content-wrapper`);

	/**
	 * Load the linked form if there is one
	 */
	useEffect(() => {
		if (form.ready && !linked.init && linked.hasChild) {
			linked.loadChild();
		}
	}, [form.ready, linked, linked.hasChild]);

	// Get the task data from context
	const task = useContext(TaskContext) as ITaskContext;

	// Scroll block errors into view
	useEffect(() => {
		if (task.validationErrors) blockFeedback.current?.scrollIntoView();
	}, [task.validationErrors]);

	// Set up our workbench context data
	const wbContext = useMemo(() => {
		return {
			workbenchIndex,
		};
	}, [workbenchIndex]);

	const close = useCallback(() => {
		if (form.dirty && task.canSaveTask) {
			setClosePrompt(true);
		} else {
			task.close();
		}
	}, [form.dirty, task]);

	// Build the task info block
	const taskInfo = useMemo(
		() => [
			{ prop: t("main.tasks.headings.assignee"), value: task.assignee },
			{
				prop: t("main.tasks.headings.dueDate"),
				value: task.dueDate
					? handleDate(task.dueDate, i18n.language, t)
					: "N/A",
			},
			{
				prop: t("main.tasks.headings.startDate"),
				value: task.startDate
					? handleDate(task.startDate, i18n.language, t)
					: "N/A",
			},
		],
		[i18n.language, t, task.assignee, task.dueDate, task.startDate]
	);

	// Build the breadcrumb for this task
	const crumb = useMemo(
		() => [
			{ name: t("main.menu.tasks"), link: "/tasks/my-tasks" },
			{ name: form.label },
		],
		[form.label, t]
	);

	useEffect(() => {
		// If scroll is bigger than the breadcrumb we set scroll is active
		formWindow?.addEventListener("scroll", () => {
			if (formWindow.scrollTop > 59) {
				setIsScrollActive(true);
			} else {
				setIsScrollActive(false);
			}
		});
	}, [formWindow]);

	const { maxWidth, maxFormWidth } = useMaxFormWidth(formLayout);

	// If the form errors, something went badly wrong
	// likely an error with a service, we can't recover from this
	// We just throw the error here and let the ErrorBoundary handle it
	if (form.error) {
		return <TaskError error={form.error} />;
	}

	// Show a loader until the form is ready to render
	if (!form.ready) return <Loading className="elem__loader--lg mt-5" />;

	return (
		<>
			<WorkbenchLayer>
				<Breadcrumb
					crumb={crumb}
					className="container"
					style={{ maxWidth }}
				/>
				<PageHeader
					sticky
					title={task.name || ""}
					scroll={isScrollActive}
					style={{ maxWidth }}
					className="page-header__title container">
					<div className="page-header__actions">
						{(form.patching || task.busy) && (
							<Loading className="elem__loader--md me-3" />
						)}
						<Button
							className="btn-outline-primary"
							disabled={form.patching || task.busy}
							onClick={close}
							label={t("common.labels.close")}
						/>
						{task.canSaveTask && (
							<Button
								className="btn-primary"
								disabled={form.patching || task.busy}
								onClick={task.save}
								label={t("common.labels.save")}
							/>
						)}
						<Button
							disabled={form.patching}
							aria-label={t("common.labels.continue")}
							name="Continue"
							label={t("common.labels.continue")}
							className="btn-primary"
							onClick={task.complete}
						/>
						{(task.canBeUnassigned || task.canDeleteActivity) && (
							<Dropdown
								className="page-header__actions--taskOptions"
								id="activityForm__dropdown"
								menuBtn={
									<IconButton
										className="btn-primary shadow"
										icon="ellipsis-v"
										label={t("main.icons.menuOptions")}
									/>
								}
								align="right">
								{task.canBeUnassigned && (
									<DropdownMenuButton
										disabled={form.patching || task.busy}
										onClick={task.unassignTask}
										label={t(
											"main.tasks.labels.unassignTask"
										)}
									/>
								)}

								{/* TODO: maybe update this implementation to not have the includes temporary and instead in the task title call pull out canDeleteActivity */}
								{task.canDeleteActivity &&
									form.formLocation?.includes(
										"/temporary/"
									) && (
										<DropdownMenuButton
											disabled={
												form.patching || task.busy
											}
											onClick={task.deleteActivity}
											label={t(
												"main.tasks.labels.deleteActivity"
											)}
										/>
									)}
							</Dropdown>
						)}
					</div>
				</PageHeader>
				<TaskInfo
					items={taskInfo}
					style={{
						maxWidth,
					}}
				/>
				<div
					className="page-content-container container  mt-1"
					style={{
						maxWidth,
					}}>
					<div className="page-content">
						<WorkbenchContext.Provider value={wbContext}>
							<TaskPageTemplate
								style={{
									maxWidth: maxFormWidth,
								}}>
								<header>
									<h2 className="form__label">
										{form.label}
									</h2>
								</header>

								<div id="block-feedback" ref={blockFeedback}>
									{task.validationErrors && (
										<div
											className="alert show block-alert block-alert--error"
											role="alert">
											<div className="alert__message">
												<div className="alert__title">
													{t(
														"alerts.missingIncorrectData.title"
													)}
												</div>
												<div className="alert__desc">
													<p className="mb-0">
														{t(
															"alerts.missingIncorrectData.description"
														)}
													</p>
												</div>
											</div>
										</div>
									)}
								</div>

								<Element
									rowIndex={0}
									fid={fid}
									uuid={form.uuid}
								/>
							</TaskPageTemplate>
							<Button
								disabled={form.patching}
								aria-label={t("common.labels.submit")}
								name="Continue"
								label={t("common.labels.continue")}
								className="btn-primary task__complete-btn d-block m-auto"
								onClick={task.complete}
							/>
						</WorkbenchContext.Provider>
					</div>
				</div>
			</WorkbenchLayer>

			{closePrompt && (
				<UnsavedChanges
					closeBtnClick={task.close}
					saveAndCloseBtnClick={task.saveAndClose}
					cancelBtnClick={() => setClosePrompt(false)}
				/>
			)}

			{linked.init && (
				<WorkbenchLayer>
					{linked.child ? (
						<LinkedForm
							fid={linked.child}
							workbenchIndex={workbenchIndex + 1}
							breadcrumb={crumb}
						/>
					) : (
						<div className="workbench-layer__editor">
							<Loading className="elem__loader--lg mt-5" />
						</div>
					)}
				</WorkbenchLayer>
			)}
		</>
	);
}

export default React.memo(TaskForm);
